import { useEffect, useId, useState, type ReactNode } from "react";

import { REASON_MIN, reasonProblem } from "./reason";

import {
  Button,
  Description,
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  Field,
  FieldGroup,
  Input,
  Label,
  Textarea,
} from "../../f7";

/**
 * Three confirmation levels, used consistently across every write:
 *
 *   L1  inline confirm in place        — reversible, small blast radius
 *   L2  modal + reason + effect timing — the default for a real change
 *   L3  L2 + type an exact literal     — three cases only
 *
 * L3 stays at exactly three (wildcard gate, deleting a `*` row, apply in
 * prod). If everything is type-to-confirm, nothing is.
 */
export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** What this changes, in the operator's terms. The blast radius sentence. */
  children: ReactNode;
  /** When the change reaches merchants. From the API's own number, never guessed. */
  effect?: ReactNode;
  confirmLabel: string;
  /** L3: the exact string that must be typed. */
  typeToConfirm?: string;
  /** L2 default: a reason is required and goes onto the audit record. */
  requireReason?: boolean;
  reasonHint?: string;
  destructive?: boolean;
  busy?: boolean;
  error?: ReactNode;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  children,
  effect,
  confirmLabel,
  typeToConfirm,
  requireReason = false,
  reasonHint,
  destructive = false,
  busy = false,
  error,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");
  const reasonProblemId = useId();

  useEffect(() => {
    if (open) {
      setReason("");
      setTyped("");
    }
  }, [open]);

  // The rule lives in `reason.ts` so it can be tested without rendering a
  // dialog, and so the same floor applies anywhere else a reason is taken.
  // A non-empty check was not enough: "x" passed it and told a later reader
  // nothing, which is the failure S0.5 exists to prevent.
  const problem = requireReason ? reasonProblem(reason) : null;
  const reasonOk = problem === null;
  // Only once they have started typing — a red hint on an untouched field
  // reads as an error the operator has already made.
  const showProblem = problem !== null && reason.length > 0;
  const typedOk = !typeToConfirm || typed.trim() === typeToConfirm;
  const canConfirm = reasonOk && typedOk && !busy;

  // F7's Dialog, which is what the design's every write flow uses. The
  // hand-rolled version reimplemented the overlay, the escape key and the focus
  // trap, and got the last one wrong — nothing constrained Tab, so an operator
  // could tab out of a destructive confirm into the page behind it. It also
  // meant every dialog in the console was the one surface the design system did
  // not draw: a `rounded-md` box with a raw `<textarea>`, beside forms built
  // out of F7 Fields everywhere else.
  return (
    <Dialog open={open} onClose={() => !busy && onCancel()}>
      <DialogTitle>{title}</DialogTitle>
      <DialogBody>
        <div className="space-y-2 text-sm/6 text-slate-500 dark:text-slate-400">{children}</div>

        {/* Always from the response's own number — a hardcoded "instant" is
            how a console lies about a 5-minute cache. */}
        {effect && (
          <p className="mt-4 rounded-lg bg-slate-100 p-3 text-sm/6 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300">
            {effect}
          </p>
        )}

        {(requireReason || typeToConfirm) && (
          <div className="mt-4">
            <FieldGroup>
              {requireReason && (
                <Field>
                  <Label>Reason</Label>
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    maxLength={500}
                    rows={2}
                    placeholder={reasonHint ?? "Why is this changing?"}
                    aria-invalid={showProblem || undefined}
                    aria-describedby={showProblem ? reasonProblemId : undefined}
                  />
                  {showProblem ? (
                    // Not `Description`: it takes no `id`, and without one
                    // `aria-describedby` on the textarea points at nothing —
                    // so the operator least able to see the amber text is the
                    // one who would never hear it.
                    <p
                      id={reasonProblemId}
                      className="mt-1 text-xs text-amber-700 dark:text-amber-400"
                    >
                      {problem}
                    </p>
                  ) : (
                    <Description>
                      Recorded on the audit trail. At least {REASON_MIN} characters, max 500.
                      A ticket key such as <span className="ref">DA-1234</span> becomes a link.
                    </Description>
                  )}
                </Field>
              )}

              {typeToConfirm && (
                <Field>
                  <Label>Type {typeToConfirm} to confirm</Label>
                  <Input
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    autoComplete="off"
                    aria-label={`Type ${typeToConfirm} to confirm`}
                  />
                </Field>
              )}
            </FieldGroup>
          </div>
        )}

        {error && <div className="mt-4">{error}</div>}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          color={destructive ? "red" : undefined}
          onClick={() => onConfirm(reason.trim())}
          disabled={!canConfirm}
        >
          {busy ? "Working…" : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
